import React, { useState, useEffect, useRef } from "react";
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { getItem } from "../../../utils/localStorage";
import { addBillerAddressURL } from '../../../utils/urls';

const BillerAddressTab = ({ onComplete, isCompleted, existingData }) => {
    const [loading, setLoading] = useState(false);
    const [websiteError, setWebsiteError] = useState('');
    const [logoPreview, setLogoPreview] = useState('');
    const fileInputRef = useRef(null);

    const [image, setImage] = useState(null);

    const [billerAddress, setBillerAddress] = useState({
        name: '',
        companyName: '',
        website: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        country: 'Kenya',
        postalCode: '',
        logo: null,
        digitalSignature: null,
        isDefault: false
    });

    // 🔹 Prefill biller address if existingData is provided
    useEffect(() => {
        if (existingData) {
            setBillerAddress({
                name: existingData.name || '',
                companyName: existingData.companyName || '',
                website: existingData.website || '',
                email: existingData.email || '',
                phone: existingData.phone || '',
                address: existingData.address || '',
                city: existingData.city || '',
                state: existingData.state || '',
                country: existingData.country || 'Kenya',
                postalCode: existingData.postalCode || '',
                logo: null, // keep file null unless user uploads a new one
                digitalSignature: null,
                isDefault: existingData.isDefault || false
            });

            // If there's an existing logo URL, set it as preview
            if (existingData.logoUrl) {
                setLogoPreview(existingData.logoUrl);
            }
        }
    }, [existingData]);

    // Website validation function
    const validateWebsiteUrl = (url) => {
        if (!url) return { isValid: true, message: '' };

        const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/;
        if (!urlPattern.test(url)) {
            return { isValid: false, message: 'Please enter a valid website URL' };
        }
        return { isValid: true, message: '' };
    };

    // Handle website change with validation
    const handleWebsiteChange = (e) => {
        const value = e.target.value;
        setBillerAddress({ ...billerAddress, website: value });

        if (value) {
            const validation = validateWebsiteUrl(value);
            setWebsiteError(validation.isValid ? '' : validation.message);
        } else {
            setWebsiteError('');
        }
    };

    // Handle file change for logo upload
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type and size
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                toastify('Please select a valid image file (JPEG, PNG, GIF)', 'error');
                return;
            }

            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                toastify('File size must be less than 5MB', 'error');
                return;
            }

            setBillerAddress({ ...billerAddress, logo: file });

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => setLogoPreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const validateBillerAddress = () => {
        return billerAddress.name &&
            billerAddress.companyName &&
            billerAddress.email &&
            billerAddress.address &&
            billerAddress.city &&
            billerAddress.country;
    };

    // const handleBillerAddressSubmit = async (e) => {
    //     e.preventDefault();

    //     // Validate website before submitting
    //     const websiteValidation = validateWebsiteUrl(billerAddress.website);
    //     if (!websiteValidation.isValid) {
    //         toastify(websiteValidation.message, 'error');
    //         return;
    //     }

    //     setLoading(true);

    //     try {
    //         const facilityId = await getItem('selectedFacilityId');
    //         const formData = new FormData();
    //         Object.keys(billerAddress).forEach(key => {
    //             if (key === 'logo' && billerAddress[key] instanceof File) {
    //                 formData.append('logo', billerAddress[key]);
    //             } else if (key !== 'logo') {
    //                 formData.append(key, billerAddress[key]);
    //             }
    //         });

    //         const url = `${addBillerAddressURL}/${facilityId}`;
    //         const response = await makeRequest2(url, 'POST', formData, {
    //             'Content-Type': 'multipart/form-data'
    //         });

    //         if (response && (response.success || response.message)) {
    //             toastify('Biller address added successfully', 'success');
    //             onComplete(); // Mark this step as completed
    //         } else {
    //             const errorMessage = response?.error || response?.message || 'Operation failed';
    //             toastify(errorMessage, 'error');
    //         }
    //     } catch (error) {
    //         console.error('Request Error:', error);
    //         if (error.response) {
    //             const errorMessage = error.response.data?.error ||
    //                 error.response.data?.message ||
    //                 `Server error: ${error.response.status}`;
    //             toastify(errorMessage, 'error');
    //         } else if (error.request) {
    //             toastify('Network error: Unable to reach server', 'error');
    //         } else {
    //             toastify('Error saving biller address', 'error');
    //         }
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleBillerAddressSubmit = async (e) => {
        e.preventDefault();

        // Validate website before submitting
        const websiteValidation = validateWebsiteUrl(billerAddress.website);
        if (!websiteValidation.isValid) {
            toastify(websiteValidation.message, 'error');
            return;
        }

        setLoading(true);

        try {
            const facilityId = await getItem('selectedFacilityId');
            const formData = new FormData();

            // Append all biller address fields
            Object.keys(billerAddress).forEach(key => {
                formData.append(key, billerAddress[key]);
            });

            // Append digital signature file if selected
            if (image) {
                formData.append('digitalSignature', image);
            }

            const url = `${addBillerAddressURL}/${facilityId}`;
            const response = await makeRequest2(url, 'POST', formData, {
                'Content-Type': 'multipart/form-data'
            });

            if (response && (response.success || response.message)) {
                toastify('Biller address added successfully', 'success');
                onComplete(); // Mark this step as completed
            } else {
                const errorMessage = response?.error || response?.message || 'Operation failed';
                toastify(errorMessage, 'error');
            }
        } catch (error) {
            console.error('Request Error:', error);
            if (error.response) {
                const errorMessage = error.response.data?.error ||
                    error.response.data?.message ||
                    `Server error: ${error.response.status}`;
                toastify(errorMessage, 'error');
            } else if (error.request) {
                toastify('Network error: Unable to reach server', 'error');
            } else {
                toastify('Error saving biller address', 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tab-pane fade show active">
            <h4 className="mb-4">Billing Address</h4>
            {isCompleted && !existingData && (
                <div className="alert alert-info mt-3">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Biller address has been saved successfully!
                </div>
            )}
            <form onSubmit={handleBillerAddressSubmit}>
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label htmlFor="addressName" className="form-label">Address Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            id="addressName"
                            value={billerAddress.name}
                            onChange={(e) => setBillerAddress({ ...billerAddress, name: e.target.value })}
                            placeholder="e.g., Head Office, Branch Office"
                            required
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label htmlFor="companyName" className="form-label">Company Name *</label>
                        <input
                            type="text"
                            className="form-control"
                            id="companyName"
                            value={billerAddress.companyName}
                            onChange={(e) => setBillerAddress({ ...billerAddress, companyName: e.target.value })}
                            placeholder="Swan Facilities Ltd"
                            required
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label htmlFor="website" className="form-label">Website</label>
                    <input
                        type="text"
                        className={`form-control ${websiteError ? 'is-invalid' : ''}`}
                        id="website"
                        value={billerAddress.website}
                        onChange={handleWebsiteChange}
                        placeholder="https://www.swanfacilities.com or www.swanfacilities.com"
                    />
                    <small className="text-muted">
                        Optional. Enter full URL (e.g., https://www.example.com, www.example.com, or example.com)
                    </small>
                    {websiteError && (
                        <div className="invalid-feedback d-block">
                            {websiteError}
                        </div>
                    )}
                </div>

                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label htmlFor="email" className="form-label">Email *</label>
                        <input
                            type="email"
                            className="form-control"
                            id="email"
                            value={billerAddress.email}
                            onChange={(e) => setBillerAddress({ ...billerAddress, email: e.target.value })}
                            placeholder="info@swanfacilities.com"
                            required
                        />
                    </div>
                    <div className="col-md-6 mb-3">
                        <label htmlFor="phone" className="form-label">Phone</label>
                        <input
                            type="tel"
                            className="form-control"
                            id="phone"
                            value={billerAddress.phone}
                            onChange={(e) => setBillerAddress({ ...billerAddress, phone: e.target.value })}
                            placeholder="+254 XXX XXX XXX"
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label htmlFor="address" className="form-label">Address *</label>
                    <textarea
                        className="form-control"
                        id="address"
                        rows="3"
                        value={billerAddress.address}
                        onChange={(e) => setBillerAddress({ ...billerAddress, address: e.target.value })}
                        placeholder="Street address, building, floor, etc."
                        required
                    ></textarea>
                </div>

                <div className="row">
                    <div className="col-md-4 mb-3">
                        <label htmlFor="city" className="form-label">City *</label>
                        <input
                            type="text"
                            className="form-control"
                            id="city"
                            value={billerAddress.city}
                            onChange={(e) => setBillerAddress({ ...billerAddress, city: e.target.value })}
                            placeholder="Nairobi"
                            required
                        />
                    </div>
                    <div className="col-md-4 mb-3">
                        <label htmlFor="state" className="form-label">State/Province</label>
                        <input
                            type="text"
                            className="form-control"
                            id="state"
                            value={billerAddress.state}
                            onChange={(e) => setBillerAddress({ ...billerAddress, state: e.target.value })}
                            placeholder="Nairobi County"
                        />
                    </div>
                    <div className="col-md-4 mb-3">
                        <label htmlFor="postalCode" className="form-label">Postal Code</label>
                        <input
                            type="text"
                            className="form-control"
                            id="postalCode"
                            value={billerAddress.postalCode}
                            onChange={(e) => setBillerAddress({ ...billerAddress, postalCode: e.target.value })}
                            placeholder="00100"
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label htmlFor="country" className="form-label">Country *</label>
                    <input
                        type="text"
                        className="form-control"
                        id="country"
                        value={billerAddress.country}
                        onChange={(e) => setBillerAddress({ ...billerAddress, country: e.target.value })}
                        required
                    />
                </div>

                {/* <div className="mb-3">
                    <label htmlFor="logo" className="form-label">Company Logo</label>
                    <input
                        type="file"
                        className="form-control"
                        id="logo"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="image/jpeg,image/jpg,image/png,image/gif"
                    />
                    <small className="text-muted">
                        Supported formats: JPEG, PNG, GIF. Max size: 5MB
                    </small>

                    {logoPreview && (
                        <div className="mt-2">
                            <img
                                src={logoPreview}
                                alt="Logo preview"
                                style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'contain' }}
                                className="border rounded"
                            />
                        </div>
                    )}
                </div> */}

                <div className="mb-3">
                    <label htmlFor="digitalSignature" className="form-label">Digital Signature</label>
                    <input
                        type="file"
                        className="form-control"
                        id="digitalSignature"
                        onChange={(e) => setImage(e.target.files[0])}
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml"
                    />

                    {/* {signaturePreview && (
                        <div className="mt-2">
                            <img
                                src={signaturePreview}
                                alt="Signature preview"
                                style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain' }}
                                className="border rounded"
                            />
                        </div>
                    )} */}
                </div>


                <div className="form-check mb-3">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={billerAddress.isDefault}
                        onChange={(e) => setBillerAddress({ ...billerAddress, isDefault: e.target.checked })}
                        id="defaultBillerAddress"
                    />
                    <label className="form-check-label" htmlFor="defaultBillerAddress">
                        Set as Default Biller Address
                    </label>
                </div>

                <div className="text-end">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading || !validateBillerAddress() || websiteError}
                    >
                        {loading ? 'Saving...' : 'Save Biller Address'}
                    </button>
                </div>

                <small className="text-muted">* Required fields</small>
            </form>
            {isCompleted && existingData && (
                <div className="alert alert-success mt-3">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Biller Address has been saved successfully!
                </div>
            )}
        </div>
    );
};

export default BillerAddressTab;